import { API_BASE } from '../../services/core.api';
import { resolveApiErrorMessage } from '../../services/api-errors';
import { onLocaleChange, t } from '../../services/i18n';
import './rc-help-view';
import { RcHelpView } from './rc-help-view';

export class RcHelp extends HTMLElement {
  private static readonly contactHash = '#contact-form';
  private state: 'idle' | 'loading' | 'success' | 'error' = 'idle';
  private errorMessage = '';
  private unsubscribeLocale: (() => void) | null = null;

  connectedCallback(): void {
    this.addEventListener('help-submit', this.handleHelpSubmit);
    this.addEventListener('help-reset', this.handleHelpReset);
    this.render();
    this.scrollToHashTarget();
    this.unsubscribeLocale = onLocaleChange(() => this.render());
  }

  disconnectedCallback(): void {
    this.unsubscribeLocale?.();
    this.removeEventListener('help-submit', this.handleHelpSubmit);
    this.removeEventListener('help-reset', this.handleHelpReset);
  }

  private render(): void {
    this.innerHTML = '<rc-help-view></rc-help-view>';
    const view = this.querySelector('rc-help-view') as RcHelpView | null;
    if (!view) return;
    view.state = this.state;
    view.errorMessage = this.errorMessage;
    view.render();
  }

  private scrollToHashTarget(): void {
    if (window.location.hash !== RcHelp.contactHash) {
      return;
    }

    requestAnimationFrame(() => {
      this.querySelector<HTMLElement>(RcHelp.contactHash)?.scrollIntoView({ block: 'start' });
    });
  }

  private handleHelpReset = (): void => {
    this.state = 'idle';
    this.errorMessage = '';
    this.render();
  };

  private handleHelpSubmit = async (event: Event): Promise<void> => {
    const form = (event as CustomEvent<{ form: HTMLFormElement }>).detail.form;
    const fd = new FormData(form);
    const name = (fd.get('name') as string).trim();
    const email = (fd.get('email') as string).trim();
    const subject = (fd.get('subject') as string).trim();
    const message = (fd.get('message') as string).trim();

    this.state = 'loading';
    this.errorMessage = '';
    this.render();

    try {
      const res = await fetch(`${API_BASE}/help/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, subject, message }),
      });

      if (res.ok) {
        this.state = 'success';
        this.render();
        return;
      }

      const body = await res.json().catch(() => ({}));
      this.errorMessage = resolveApiErrorMessage(body, res.status, 'help.form.error');
      this.state = 'error';
    } catch {
      this.errorMessage = t('help.form.networkError');
      this.state = 'error';
    }

    this.render();
    const nameEl = this.querySelector<HTMLInputElement>('#help-name');
    const emailEl = this.querySelector<HTMLInputElement>('#help-email');
    const subjectEl = this.querySelector<HTMLInputElement>('#help-subject');
    const messageEl = this.querySelector<HTMLTextAreaElement>('#help-message');
    if (nameEl) nameEl.value = name;
    if (emailEl) emailEl.value = email;
    if (subjectEl) subjectEl.value = subject;
    if (messageEl) messageEl.value = message;
  };
}

if (!customElements.get('rc-help')) {
  customElements.define('rc-help', RcHelp);
}
